// LabelEditor — Phase 6c. Pill-chip row + "Add label ▾" dropdown.
//
// Rendered in the Command Center next to the StatusPill so Kimi can tag
// a conversation as "Refund", "Proof Change", etc. without leaving the
// thread. Labels live on conversation_labels (Phase 6c migration).
//
// Behavior:
//   - Existing labels show as monstera-tinted chips, each with an × button
//     that fires useRemoveLabel.
//   - "Add label ▾" opens a popover with the 8 recommended labels +
//     a free-form text input at the bottom for custom tags.
//   - Recommended labels already applied are dimmed in the menu so Kimi
//     sees at a glance what's left to pick.
//   - Outside-click + Escape dismiss, matching the BulkActionBar pattern.

import { useEffect, useRef, useState } from 'react';
import {
  useConversationLabels,
  useAddLabel,
  useRemoveLabel,
  RECOMMENDED_LABELS,
} from '@/lib/hooks/useConversationLabels';

type LabelEditorProps = {
  conversationId: string;
};

export function LabelEditor({ conversationId }: LabelEditorProps) {
  const [open, setOpen] = useState(false);
  const [customInput, setCustomInput] = useState('');
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const { data: labels = [], isLoading } = useConversationLabels(conversationId);
  const addMutation = useAddLabel(conversationId);
  const removeMutation = useRemoveLabel(conversationId);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && e.target instanceof Node && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const appliedSet = new Set(labels.map((l) => l.label.toLowerCase()));

  const pickLabel = (label: string) => {
    const trimmed = label.trim();
    if (!trimmed) return;
    addMutation.mutate(trimmed, {
      onSuccess: () => {
        setCustomInput('');
        setOpen(false);
      },
    });
  };

  const busy = addMutation.isPending || removeMutation.isPending;

  return (
    <div
      ref={wrapRef}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
      }}
    >
      {isLoading ? null : (
        labels.map((l) => (
          <span
            key={l.id}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '3px 8px 3px 10px',
              borderRadius: 999,
              background: 'hsl(var(--monstera) / 0.1)',
              color: 'hsl(var(--monstera))',
              border: '1px solid hsl(var(--monstera) / 0.3)',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {l.label}
            <button
              type="button"
              onClick={() => removeMutation.mutate(l.id)}
              disabled={busy}
              aria-label={`Remove ${l.label} label`}
              title="Remove label"
              style={{
                border: 'none',
                background: 'transparent',
                color: 'hsl(var(--monstera))',
                cursor: busy ? 'not-allowed' : 'pointer',
                padding: 0,
                lineHeight: 1,
                fontSize: 14,
                marginLeft: 2,
              }}
            >
              ×
            </button>
          </span>
        ))
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          padding: '4px 10px',
          borderRadius: 999,
          border: '1px dashed hsl(var(--ink) / 0.3)',
          background: 'transparent',
          color: 'hsl(var(--ink) / 0.6)',
          fontSize: 12,
          fontWeight: 500,
          cursor: busy ? 'not-allowed' : 'pointer',
        }}
        title="Add a label to this thread"
      >
        + Add label
      </button>

      {open ? (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            background: 'hsl(var(--stone))',
            border: '1px solid hsl(var(--ink) / 0.12)',
            borderRadius: 10,
            boxShadow: '0 10px 30px hsl(var(--ink) / 0.12)',
            minWidth: 220,
            padding: 6,
            zIndex: 30,
          }}
        >
          {RECOMMENDED_LABELS.map((name) => {
            const already = appliedSet.has(name.toLowerCase());
            return (
              <button
                key={name}
                type="button"
                role="menuitem"
                disabled={busy || already}
                onClick={() => pickLabel(name)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 6,
                  cursor: already || busy ? 'not-allowed' : 'pointer',
                  color: already ? 'hsl(var(--ink) / 0.35)' : 'hsl(var(--ink))',
                  fontSize: 14,
                }}
                onMouseEnter={(e) => {
                  if (already || busy) return;
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'hsl(var(--ink) / 0.04)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                {name}{already ? ' ✓' : ''}
              </button>
            );
          })}
          <div
            style={{
              borderTop: '1px solid hsl(var(--ink) / 0.08)',
              marginTop: 6,
              paddingTop: 8,
              paddingLeft: 8,
              paddingRight: 8,
              paddingBottom: 6,
            }}
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                pickLabel(customInput);
              }}
              style={{ display: 'flex', gap: 6 }}
            >
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Custom label…"
                disabled={busy}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid hsl(var(--ink) / 0.15)',
                  background: 'white',
                  fontSize: 13,
                  color: 'hsl(var(--ink))',
                }}
              />
              <button
                type="submit"
                disabled={busy || !customInput.trim()}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: '1px solid hsl(var(--monstera) / 0.4)',
                  background: 'hsl(var(--monstera) / 0.1)',
                  color: 'hsl(var(--monstera))',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor:
                    busy || !customInput.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                Add
              </button>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
